import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  CreditCard,
  Calculator,
  Briefcase,
  Plug,
  Check,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';

/**
 * IntegrationsPage
 * --------------------------------------------------------------
 * Worqly's integrations directory. Stripe is the only live
 * integration today (via the Payments module); everything else
 * is honestly labelled "Coming soon" with a notify-me CTA.
 *
 * Visual language matches the rest of the marketing site.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

type Status = 'Active' | 'Coming soon';

type Integration = {
  name: string;
  blurb: string;
  status: Status;
  Mark: React.FC<{ className?: string }>;
  note?: string;
};

type Category = {
  id: string;
  title: string;
  eyebrow: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  integrations: Integration[];
};

/* ─────────────────────────────────────────────────────────────
 * Tiny inline brand marks (typographic only — no logo files).
 * Honest illustration; no scraped vendor SVGs.
 * ───────────────────────────────────────────────────────────── */

function BrandMark({
  letters,
  className,
  bg = 'bg-ink-900 text-white',
}: {
  letters: string;
  className?: string;
  bg?: string;
}) {
  return (
    <span
      className={
        'inline-flex h-11 w-11 items-center justify-center rounded-2xl text-[14px] font-bold tracking-tight ' +
        bg +
        (className ? ' ' + className : '')
      }
    >
      {letters}
    </span>
  );
}

const Marks = {
  Stripe:   (p: { className?: string }) => <BrandMark letters="Str" bg="bg-[#635BFF] text-white" {...p} />,
  Razorpay: (p: { className?: string }) => <BrandMark letters="Rz"  bg="bg-[#3395FF] text-white" {...p} />,
  Wise:     (p: { className?: string }) => <BrandMark letters="W"   bg="bg-[#9FE870] text-ink"   {...p} />,
  QB:       (p: { className?: string }) => <BrandMark letters="QB"  bg="bg-[#2CA01C] text-white" {...p} />,
  Xero:     (p: { className?: string }) => <BrandMark letters="X"   bg="bg-[#13B5EA] text-white" {...p} />,
  Slack:    (p: { className?: string }) => <BrandMark letters="Sl"  bg="bg-[#4A154B] text-white" {...p} />,
  Google:   (p: { className?: string }) => <BrandMark letters="G"   bg="bg-white text-[#4285F4] ring-1 ring-ink/8 dark:ring-white/15" {...p} />,
  MS365:    (p: { className?: string }) => <BrandMark letters="M"   bg="bg-[#E8A33D] text-white" {...p} />,
};

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

const CATEGORIES: Category[] = [
  {
    id: 'payments',
    title: 'Payments',
    eyebrow: 'Get paid',
    description: 'Take card payments on your invoices without leaving Worqly.',
    Icon: CreditCard,
    integrations: [
      {
        name: 'Stripe',
        blurb: 'Connect your own Stripe account with your API keys. Worqly creates a Stripe Checkout card link per invoice and reconciles the payment automatically; payouts run on your Stripe schedule.',
        status: 'Active',
        Mark: Marks.Stripe,
        note: 'Live in every workspace',
      },
      {
        name: 'Razorpay',
        blurb: 'UPI, cards, netbanking, and wallets for Indian workspaces. Same one-click pay links, same auto-reconcile.',
        status: 'Coming soon',
        Mark: Marks.Razorpay,
      },
      {
        name: 'Wise',
        blurb: 'Multi-currency invoice payouts and FX-fair conversions for cross-border teams.',
        status: 'Coming soon',
        Mark: Marks.Wise,
      },
    ],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    eyebrow: 'Book it',
    description: 'Push invoices, payments, payroll, and expenses to your books — no CSV gymnastics.',
    Icon: Calculator,
    integrations: [
      {
        name: 'QuickBooks Online',
        blurb: 'Two-way sync for customers, invoices, payments, and chart of accounts. Pick the mapping once.',
        status: 'Coming soon',
        Mark: Marks.QB,
      },
      {
        name: 'Xero',
        blurb: 'Contacts, invoices, payments, and payroll journals pushed to Xero on the cadence you choose.',
        status: 'Coming soon',
        Mark: Marks.Xero,
      },
    ],
  },
  {
    id: 'productivity',
    title: 'Productivity',
    eyebrow: 'Where your team lives',
    description: 'Stay in the tools your team already uses — Worqly events surface where they belong.',
    Icon: Briefcase,
    integrations: [
      {
        name: 'Slack',
        blurb: 'Invoice paid? Receipt awaiting approval? Time entry flagged? Right channel, right person, right now.',
        status: 'Coming soon',
        Mark: Marks.Slack,
      },
      {
        name: 'Sign in with Google',
        blurb: 'One-tap Google sign-in and sign-up for your team — auto-linked to your workspace by verified email.',
        status: 'Active',
        Mark: Marks.Google,
      },
      {
        name: 'Google Workspace',
        blurb: 'Calendar sync for leave + shifts, and Drive backup of every invoice + payslip.',
        status: 'Coming soon',
        Mark: Marks.Google,
      },
      {
        name: 'Microsoft 365',
        blurb: 'Entra ID sign-in, Outlook Calendar for leave + shifts, OneDrive backups of PDFs.',
        status: 'Coming soon',
        Mark: Marks.MS365,
      },
    ],
  },
];

export function IntegrationsPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Integrations"
        description="Connect Worqly to the tools you already use — Stripe payments today; accounting, banking, and identity providers landing soon."
      />
      <Hero />
      <ActiveCount />
      {CATEGORIES.map((c, i) => (
        <CategoryGrid key={c.id} category={c} tinted={i % 2 === 1} />
      ))}
      <RequestSection />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Plug className="h-3 w-3 text-coral" />
              Integrations
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            Worqly works with the tools{' '}
            <span className="italic font-serif text-coral">you already use.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Live today: Stripe payments and Google sign-in. The rest are on the roadmap with dates we
            actually hit — not vapourware. Tell us which one matters most, we move it up.
          </motion.p>

          <motion.div variants={FADE_UP} className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Start free
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="mailto:hello@worqly.io?subject=Integration%20request"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
            >
              Request an integration
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Active count strip
 * ───────────────────────────────────────────────────────────── */

function ActiveCount() {
  const totals = CATEGORIES.flatMap((c) => c.integrations);
  const active = totals.filter((i) => i.status === 'Active').length;
  const soon = totals.length - active;
  return (
    <section className="py-10 border-y border-ink/8 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] backdrop-blur-md">
      <div className="max-w-site mx-auto px-6 lg:px-8 grid grid-cols-3 gap-4">
        {[
          { v: active, l: 'Live today' },
          { v: soon,   l: 'On the roadmap' },
          { v: CATEGORIES.length, l: 'Categories' },
        ].map((s) => (
          <div key={s.l} className="text-center">
            <p className="text-stat-lg font-bold tabular-nums text-ink dark:text-white">{s.v}</p>
            <p className="mt-1 text-[12px] uppercase tracking-[0.16em] font-bold text-ink/55 dark:text-white/55">
              {s.l}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Category grid
 * ───────────────────────────────────────────────────────────── */

function CategoryGrid({ category, tinted }: { category: Category; tinted: boolean }) {
  return (
    <section
      id={category.id}
      className={
        'py-20 lg:py-24 scroll-mt-24 ' +
        (tinted
          ? 'bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5'
          : '')
      }
    >
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={STAGGER}
          className="max-w-3xl mb-10 lg:mb-12"
        >
          <motion.div variants={FADE_UP} className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400">
              <category.Icon className="h-5 w-5" />
            </span>
            <Eyebrow className="!text-coral">{category.eyebrow}</Eyebrow>
          </motion.div>
          <motion.h2
            variants={FADE_UP}
            className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em] text-balance"
          >
            {category.title}
          </motion.h2>
          <motion.p
            variants={FADE_UP}
            className="mt-4 text-[15px] text-ink/65 dark:text-white/65 max-w-2xl"
          >
            {category.description}
          </motion.p>
        </motion.div>

        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
        >
          {category.integrations.map((it) => (
            <motion.li key={it.name} variants={FADE_UP}>
              <IntegrationCard integration={it} />
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const active = integration.status === 'Active';
  return (
    <div
      className={
        'group h-full rounded-3xl p-6 transition-all bg-white/85 ring-1 ring-ink/5 shadow-card hover:shadow-card-lg ' +
        'dark:bg-white/[0.04] dark:ring-white/10 ' +
        (active ? 'relative' : '')
      }
    >
      <div className="flex items-start justify-between gap-3">
        <integration.Mark />
        <span
          className={
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.10em] shrink-0 ' +
            (active
              ? 'bg-lime-neon text-ink'
              : 'bg-ink/5 text-ink/55 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/55 dark:ring-white/10')
          }
        >
          {active && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
          {integration.status}
        </span>
      </div>
      <h3 className="mt-4 text-[18px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
        {integration.name}
      </h3>
      <p className="mt-2 text-[13.5px] text-ink/65 dark:text-white/65 leading-relaxed">
        {integration.blurb}
      </p>
      {integration.note && (
        <p className="mt-3 text-[12px] text-coral font-semibold">{integration.note}</p>
      )}
      <div className="mt-5">
        {active ? (
          <Link
            to="/features#payments"
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-coral group-hover:gap-2 transition-all"
          >
            Learn more
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          <a
            href={`mailto:hello@worqly.io?subject=Notify%20me%20when%20${encodeURIComponent(integration.name)}%20lands`}
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-ink/55 hover:text-coral dark:text-white/55 dark:hover:text-coral-400 transition-colors"
          >
            Notify me
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Request section
 * ───────────────────────────────────────────────────────────── */

function RequestSection() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-7 lg:p-10 flex flex-col md:flex-row gap-6 md:items-center"
        >
          <motion.div variants={FADE_UP} className="md:flex-1 min-w-0">
            <Eyebrow className="!text-coral">Request</Eyebrow>
            <h3 className="mt-2 text-[22px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
              Don't see your stack?
            </h3>
            <p className="mt-2 text-[14.5px] text-ink/70 dark:text-white/70 leading-relaxed">
              We prioritise the integrations our customers ask for most. Tell us what you need and
              we'll either ship it or tell you when we plan to.
            </p>
          </motion.div>
          <motion.div variants={FADE_UP} className="flex flex-wrap gap-3">
            <a
              href="mailto:hello@worqly.io?subject=Integration%20request"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Request an integration
              <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/changelog"
              className="inline-flex items-center gap-1 h-11 px-2 text-[13.5px] font-semibold text-ink/70 hover:text-coral dark:text-white/70 dark:hover:text-coral-400 transition-colors"
            >
              See changelog
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Footer CTA
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                Start free
              </p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Start with Stripe today. <em className="font-serif">Get the rest soon.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Stripe payments work the moment you sign up. Every other integration ships when
                we're confident it'll work the way you expect — not before.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:hello@worqly.io?subject=Integration%20request"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                Email us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
